'use client'
import { SbBlokData, storyblokEditable, StoryblokServerComponent } from '@storyblok/react/rsc'
import { FC, useEffect, useState } from 'react'

import { PhnxAvatar } from '@/components/01-atoms/Avatar/Avatar'
import { PhnxImageHolder } from '@/components/01-atoms/ImageHolder/ImageHolder'
import { searchParamsData } from '@/lib/sbAPI'
import { getStoryblokApi } from '@/lib/storyblok-api'

import { PhnxCard } from '../../02-molecules/Card/Card'
import { PhnxSectionheader } from '../../02-molecules/Sectionheader/Sectionheader'
import { endorsementData, officeData, PhnxAdviserPage } from '../AdviserPage/AdviserPage'
import { AdvisorProps } from './Advisor'

interface StoryblokImage {
  filename: string
  alt: string
}

interface DataSourceEntry {
  name: string
  value: string
}

interface SpecialtyTag {
  label: string
  url: string
}

interface QualificationItem {
  title: string
}

interface OfficeLocation {
  latitude?: number
  longitude?: number
}

interface OfficeData {
  address: string
  phone: string
  location?: OfficeLocation[]
}

interface OfficeContent {
  component?: string
  office?: OfficeData[]
  address?: string
  Phone?: string
  Latitude?: string
  Longitude?: string
}

interface OfficeItem {
  content?: OfficeContent
  name?: string
  slug?: string
  full_slug?: string
}

interface EndorsementContent {
  stars: number
  description: string
  name: string
  position: string
}

interface EndorsementBlok {
  item?: EndorsementContent
  stars?: number
  description?: string
  name?: string
  position?: string
}

interface ArticleStory {
  name: string
  full_slug: string
  content: {
    tags: string[]
    featured_image: StoryblokImage
  }
}

interface ProcessedArticle {
  title: string
  tags: string[]
  link: string
  asset: {
    src: string
    alt: string
  }
  author?: string
}

interface AdvisorBlokProps {
  blok: {
    image: StoryblokImage
    title: AdvisorProps['title']
    bio: SbBlokData[]
    qualifications?: QualificationItem[]
    endorsements?: EndorsementBlok[]
    specialities: string[]
    offices: OfficeItem[] | string[]
    salesforceid: string
    givenName: string
    surname: string
  } & SbBlokData
  name: string
  uuid: string
}

const Advisor: FC<AdvisorBlokProps> = ({ blok, uuid }) => {
  const [specialties, setSpecialties] = useState<SpecialtyTag[]>([])
  const [qualifications, setQualifications] = useState<React.ReactElement | null>(null)
  const [endorsements, setEndorsements] = useState<endorsementData[] | undefined>(undefined)
  const [offices, setOffices] = useState<officeData[]>([])
  const [articles, setArticles] = useState<ProcessedArticle[]>([])
  const [specialtyLinks, setSpecialtyLinks] = useState<DataSourceEntry[]>([])

  // Fetch the service lines links from the datasource
  useEffect(() => {
    const fetchSpecialtyLinks = async () => {
      const storyblokApi = getStoryblokApi()
      const { data } = await storyblokApi.get(`cdn/datasource_entries`, {
        ...searchParamsData,
        per_page: 100,
        datasource: 'service-lines-links',
      })
      setSpecialtyLinks(data.datasource_entries)
    }
    fetchSpecialtyLinks()
  }, [])

  // Combine the specialties with the links
  useEffect(() => {
    if (blok.specialities != null && blok.specialities.length > 0 && specialtyLinks.length > 0) {
      const combinedSpecialties = blok.specialities.map((item: string) => {
        const matchingLink = specialtyLinks.find((link: DataSourceEntry) => link.name === item)
        return {
          label: item,
          url:
            matchingLink != null && matchingLink.value.includes('/')
              ? matchingLink.value
              : `/advisers/search?specialty=${item}`,
        }
      })

      setSpecialties(combinedSpecialties)
    }
  }, [blok.specialities, specialtyLinks])

  // QUALIFICATIONS
  useEffect(() => {
    if (blok.qualifications != null && blok.qualifications.length > 0) {
      const listedQualifications = blok.qualifications.map(
        (item: QualificationItem, index: number) => <li key={index}>{item.title}</li>
      )
      setQualifications(<ul>{listedQualifications}</ul>)
    }
  }, [blok.qualifications])

  //ENDORSEMENTS

  useEffect(() => {
    if (blok.endorsements != null && blok.endorsements.length > 0) {
      const listedEndorsements = blok.endorsements.map((item: EndorsementBlok) => {
        const content = item?.item ?? (item as EndorsementContent)
        return {
          stars: content.stars as number,
          description: content.description,
          name: content.name,
          position: content.position,
        }
      })
      setEndorsements(listedEndorsements)
    }
  }, [blok.endorsements])

  useEffect(() => {
    if (!Array.isArray(blok.offices)) return

    const offices: officeData[] = []

    blok.offices.forEach((office: OfficeItem) => {
      const content = office?.content

      // Check if this is an advance office (nested structure)
      const isAdvanceOffice = content?.component === 'advanceOffice'

      // Helper function to clean the office slug
      const cleanOfficeSlug = (slug: string) => {
        // Remove en-nz/ or en-au/ prefix if present
        return slug.replace(/^(en-nz|en-au)\//, '')
      }

      if (isAdvanceOffice && content?.office?.[0] != null) {
        // Handle advance office format: content.office[0].address
        const advanceOfficeData = content.office[0]

        if (advanceOfficeData.address != null && advanceOfficeData.phone != null) {
          offices.push({
            title: office?.name ?? '',
            address: advanceOfficeData.address,
            phone: advanceOfficeData.phone,
            link: office?.full_slug != null ? `/${cleanOfficeSlug(office.full_slug)}` : undefined,
            location: {
              lat: advanceOfficeData.location?.[0]?.latitude ?? 0,
              long: advanceOfficeData.location?.[0]?.longitude ?? 0,
            },
          })
        }
      } else if (content?.address != null && content?.Phone != null) {
        // Handle standard office format: content.address, content.Phone
        offices.push({
          title: office?.name ?? '',
          address: content.address,
          phone: content.Phone,
          link: office?.full_slug != null ? `/${cleanOfficeSlug(office.full_slug)}` : undefined,
          location: {
            lat: Number(content.Latitude) || 0,
            long: Number(content.Longitude) || 0,
          },
        })
      }
    })

    setOffices(offices)
  }, [blok.offices])

  useEffect(() => {
    // Find articles with author matching uuid
    let location =
      process.env.NEXT_PUBLIC_NEXT_LOCATION != null ? process.env.NEXT_PUBLIC_NEXT_LOCATION : 'au'
    let filterLocation = location === 'en-nz' ? 'au' : `en-nz`

    const fetchArticles = async () => {
      let sbParams = {
        ...searchParamsData,
        filter_query: {
          component: { in: 'article' },
          author: { in: uuid },
          __or: [
            { 'settings.location_available': { in: location } },
            { 'settings.location_available': { not_in: filterLocation } },
          ],
        },
        sort_by: 'published_at:desc',
        per_page: 100,
      }

      const storyblokApi = getStoryblokApi()
      let { data } = await storyblokApi.get(`cdn/stories/`, sbParams)
      let results: ProcessedArticle[] = []
      if (data?.stories != null) {
        results = data.stories.map((story: ArticleStory) => {
          return {
            title: story.name,
            tags: story.content.tags,
            link: `/${story.full_slug}`,
            asset: {
              src: story.content.featured_image.filename,
              alt: story.content.featured_image.alt,
            },
          }
        })
      }
      setArticles(results)
    }
    fetchArticles()
  }, [uuid])

  return (
    <div {...storyblokEditable(blok)}>
      <PhnxAdviserPage
        title={blok.givenName + ' ' + blok.surname}
        header={
          <PhnxSectionheader
            title={blok.givenName + ' ' + blok.surname}
            subtitle={blok.title}
            asset={
              <PhnxImageHolder
                images={[
                  {
                    src: blok.image.filename,
                    alt: blok.image.alt,
                  },
                ]}
              />
            }
            link={{
              label: 'ADVISERS',
              url: '/advisers/search',
            }}
            tags={specialties}
          />
        }
        biography={
          blok.bio != null && blok.bio.length > 0
            ? blok.bio.map((child: SbBlokData) => (
                <StoryblokServerComponent blok={child} key={child._uid} />
              ))
            : null
        }
        qualifications={qualifications}
        officelocations={offices}
        endorsements={endorsements}
        articles={
          articles != null && articles.length > 0
            ? articles.map((article, index) => (
                <PhnxCard
                  key={'articlecard' + index}
                  variant="secondary"
                  layout="vertical"
                  alignment="default"
                  style="square"
                  feature={false}
                  asset={
                    <PhnxImageHolder
                      images={[{ src: article.asset.src, alt: article.asset.alt, focus: '' }]}
                    />
                  }
                  title={article.title}
                  tags={article.tags}
                  link={article.link}
                  author={
                    <PhnxAvatar
                      asset={{
                        src: blok.image.filename ?? '/assets/findex_contributer.png',
                        alt: blok.image.alt ?? `${blok.givenName} ${blok.surname}`,
                      }}
                      title={`By ${blok.givenName} ${blok.surname}`}
                      size="xsmall"
                      style="circle"
                    />
                  }
                />
              ))
            : null
        }
      />
    </div>
  )
}

export default Advisor
